/**
 * Top-level route definitions + auth guards.
 *
 * Public routes: /login, /accept/:token
 * Staff routes (admin + valuation_analyst): /admin/**
 * Client routes: /c/:slug   (stub)
 */

import { Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";

import { useAuth } from "./lib/useAuth";

import Login from "./routes/Login";
import AcceptInvite from "./routes/AcceptInvite";
import AdminLayout from "./routes/AdminLayout";
import AdminDashboard from "./routes/AdminDashboard";
import AdminClients from "./routes/AdminClients";
import AdminClientDetail from "./routes/AdminClientDetail";
import AdminUsers from "./routes/AdminUsers";
import ClientPortalStub from "./routes/ClientPortalStub";
import NotFound from "./routes/NotFound";

function FullPageLoader() {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
      <div className="spinner" aria-label="Loading" />
    </div>
  );
}

function StaffGuard({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && user.role !== "valuation_analyst") {
    return <Navigate to={user.role === "client" ? `/c/${user.clientId ?? ""}` : "/login"} replace />;
  }
  return <>{children}</>;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/c/me" replace />;
  return <Navigate to="/admin" replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      <Route path="/login" element={<Login />} />
      <Route path="/accept/:token" element={<AcceptInvite />} />

      <Route
        path="/admin"
        element={
          <StaffGuard>
            <AdminLayout />
          </StaffGuard>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="clients/:id" element={<AdminClientDetail />} />
        <Route path="users" element={<AdminUsers />} />
      </Route>

      <Route path="/c/:slug" element={<ClientPortalStub />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
